/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   main.tsx
 *     Description     :   React 19 application entry point — mounts root with MsalProvider and QueryClientProvider.
 *     Module          :   005-web-shell
 *     Component Type  :   Entry
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-23      Peter Dobler        Initial creation
 *     1.1         2026-07-04      Peter Dobler        Spec 016: mode switch for the standalone RP build
 *     1.2         2026-07-04      Peter Dobler        Spec 016 fold-in (operator decision): single app —
 *                                                     Remove Pharmacy is a section, mode switch removed
 * -----------------------------------------------------------------------------------------
 *
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   react react-dom @azure/msal-browser @tanstack/react-query
 *     Internal        :   (none — side-effect entry)
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     (none — side-effect entry)
 *
 * =========================================================================================
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App } from "./App.js";
import { ThemeProvider } from "./theme/index.js";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 2,
    },
  },
});

// ---------------------------------------------------------------------------------------------
// A tab open across a deploy loads chunk names that no longer exist.
//
// 🔴 THE `no-cache` HEADER ON index.html CANNOT FIX THIS ONE. That header keeps the NEXT page load
// honest. A tab already running holds its module graph in memory, so a client-side route change
// dynamically imports a chunk whose hash was baked in before the deploy — and never asks the server
// for index.html at all. Measured 2026-09-13: after deploying 1.0.74, an open tab still requested
// `RemovePharmacySection-H-ytU3lE.js` (404) while the server served `…-DwCJnnrF.js` (200).
//
// Vite fires `vite:preloadError` for exactly this. Unhandled, React surfaces it as
// "Failed to fetch dynamically imported module" and the route dies with a blank screen — which
// reads like a broken build rather than a stale tab.
//
// ⚠️ `location.reload()` is guarded by a sessionStorage flag so a genuinely missing chunk cannot
// become a reload loop. If the chunk is still absent after one reload we let the error through,
// because an infinite refresh is a worse failure than a visible one.
// ---------------------------------------------------------------------------------------------
const RELOADED_KEY = "ngm:reloaded-for-stale-chunk";
window.addEventListener("vite:preloadError", (event) => {
  if (sessionStorage.getItem(RELOADED_KEY)) {
    console.error("Chunk still missing after a reload — not reloading again.", event);
    return;
  }
  sessionStorage.setItem(RELOADED_KEY, "1");
  window.location.reload();
});
// Cleared on a successful load, so the next deploy gets its own single retry.
window.addEventListener("load", () => sessionStorage.removeItem(RELOADED_KEY));

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("Root element #root not found in index.html");
}

createRoot(rootEl).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
